import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDMY } from "@/lib/date";
import { computeRanks } from "@/lib/leaderboardRanks";
import { Trophy, Camera, Calendar, ListChecks, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const canSeeStats = user?.role !== "VIEWER";
  const [data, setData] = useState({
    photogs: 0,
    seasons: [],
    competitions: [],
    leaderboard: [],
  });

  useEffect(() => {
    (async () => {
      const [p, s, c] = await Promise.all([
        api.get("/photographers"),
        api.get("/seasons"),
        api.get("/competitions"),
      ]);
      const activeSeason = s.data.find((x) => x.is_active) || s.data[0];
      const lb = activeSeason && canSeeStats
        ? (await api.get("/stats/leaderboard", { params: { season_id: activeSeason.id, limit: 5 } })).data
        : [];
      setData({
        photogs: p.data.length,
        seasons: s.data,
        competitions: c.data.slice(0, 5),
        leaderboard: computeRanks(lb),
        activeSeason,
      });
    })();
  }, [canSeeStats]);

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="mb-6 md:mb-8">
        <div className="ccs-label mb-2">Console</div>
        <h1 className="text-2xl md:text-4xl font-semibold tracking-tight">Dashboard</h1>
        {data.activeSeason && (
          <div className="muted-text mt-2 text-sm">
            Active season:{" "}
            <span className="mono text-amber-400">{data.activeSeason.season_name}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <Tile label="Photographers" value={data.photogs} icon={Camera} to="/photographers" />
        <Tile label="Seasons" value={data.seasons.length} icon={Calendar} to="/seasons" />
        <Tile
          label="Competitions"
          value={data.competitions.length ? data.competitions.length + "+" : 0}
          icon={Trophy}
          to="/competitions"
        />
        <Tile label="Results" value="—" icon={ListChecks} to="/results" hideValue />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="ccs-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Recent Competitions</h2>
            <Link to="/competitions" className="text-amber-400 text-sm flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          {data.competitions.length === 0 ? (
            <div className="muted-text text-sm">No competitions yet.</div>
          ) : (
            <ul className="divide-y divide-neutral-900">
              {data.competitions.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{c.competition_name}</div>
                    <div className="text-xs muted-text mono">
                      {formatDMY(c.competition_date)} · {c.competition_type} · {c.category}
                    </div>
                  </div>
                  <Link
                    to={`/entry/${c.id}`}
                    className="text-sm text-amber-400 hover:text-amber-300"
                    data-testid={`dashboard-enter-${c.id}`}
                  >
                    Enter →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {canSeeStats && (
        <div className="ccs-card" data-testid="dashboard-leaderboard-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Season Leaderboard</h2>
            <Link to="/stats" className="text-amber-400 text-sm flex items-center gap-1">
              Full stats <ArrowRight size={14} />
            </Link>
          </div>
          {data.leaderboard.length === 0 ? (
            <div className="muted-text text-sm">No entries scored yet.</div>
          ) : (
            <ol className="space-y-2">
              {data.leaderboard.map((r) => (
                <li
                  key={r.photographer_id}
                  className="flex items-center justify-between py-2 px-3"
                  style={{ background: "#131313", border: "1px solid #1f1f1f" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="mono text-amber-400 w-6">#{r._rank}</div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {r.photographer_name}
                        {r._tied && (
                          <span
                            className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            title="Tied on total score — countback rules apply for the final award"
                            data-testid="dashboard-tied-pill"
                          >
                            Tied
                          </span>
                        )}
                      </div>
                      <div className="text-xs mono muted-text">{r.photographer_code}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="mono text-lg font-semibold">{r.total_score}</div>
                    <div className="text-xs muted-text">{r.entries} entries</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, to, hideValue }) {
  return (
    <Link to={to} className="stat-tile block hover:bg-neutral-900 transition-colors">
      <div className="flex items-center justify-between">
        <div>
          <div className="label">{label}</div>
          {!hideValue && <div className="value">{value}</div>}
          {hideValue && <div className="value text-neutral-600">→</div>}
        </div>
        <Icon size={28} strokeWidth={1.2} className="text-amber-400/60" />
      </div>
    </Link>
  );
}
